import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNotification } from "../context/NotificationProvider";
import { useLocalization } from "../context/LocalizeContextProvider";
import {
  getReportVisits,
  exportReportVisits,
} from "../services/ReportVisitService";
import { PER_PAGE_SIZE } from "../utils/constants";
import moment from "moment";
import { downloadBlob } from "../utils/utils";

export default function useReportVisit() {
  const notification = useNotification();
  const localize = useLocalization();
  const defaultFilter = {
    type: { label: "Harian", value: "day" },
    from: moment().startOf("month").format("YYYY-MM-DD"),
    to: moment().endOf("month").format("YYYY-MM-DD"),
    page: 0,
    size: PER_PAGE_SIZE,
    doctor_id: null,
  };

  const [totalPage, setTotalPage] = useState(0);
  const [totalEntries, setTotalEntries] = useState(0);
  const [dataReportVisit, setDataReportVisit] = useState([]);
  const [filterReport, setFilterReport] = useState(defaultFilter);

  const { isLoading, mutate: mutateReport } = useMutation(
    ["GetReportVisits"],
    (data) => getReportVisits(data),
    {
      onSuccess: (res) => {
        if (res.status === 200 || res.status === 201) {
          setDataReportVisit(res?.data);
          setTotalEntries(Number(res.headers["x-total-count"]));
          setTotalPage(
            Math.ceil(Number(res.headers["x-total-count"]) / filterReport.size)
          );
        } else {
          notification.error(
            res?.data?.detail || localize.getText("unknownMessageError")
          );
        }
      },
    }
  );

  const { isLoading: isLoadingExport, mutate: mutateExportReport } =
    useMutation(["ExportReportVisits"], (data) => exportReportVisits(data), {
      onSuccess: (res) => {
        if (res.status === 200 || res.status === 201) {
          downloadBlob(res.data, "report-visit");
        } else {
          notification.error(
            res?.data?.detail || localize.getText("unknownMessageError")
          );
        }
      },
    });

  useEffect(() => {
    mutateReport(filterReport);
    // eslint-disable-next-line
  }, []);

  const handleChangeFilter = async (name, value) => {
    if (name === "type") {
      await setFilterReport((prevState) => ({
        ...prevState,
        from: null,
        to: null,
      }));
    } else if (
      name === "from" &&
      filterReport.to &&
      moment(value, "YYYY-MM-DD").isAfter(moment(filterReport.to, "YYYY-MM-DD"))
    ) {
      await setFilterReport((prevState) => ({
        ...prevState,
        to: null,
      }));
    }
    setFilterReport((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleClickFilter = () => {
    handleChangeFilter("page", 0);
    mutateReport({
      ...filterReport,
      doctor_id: filterReport?.doctor_id?.value,
      page: 0,
    });
  };

  const handleChangeSizePerPage = (size) => {
    handleChangeFilter("size", size);
    handleChangeFilter("page", 0);
    mutateReport({
      ...filterReport,
      size,
      page: 0,
      doctor_id: filterReport?.doctor_id?.value,
    });
  };

  const handlePagination = (value) => {
    handleChangeFilter("page", value);
    mutateReport({
      from: filterReport.from,
      to: filterReport.to,
      page: value,
      size: filterReport.size,
      doctor_id: filterReport?.doctor_id?.value,
    });
  };

  const handleClearFilter = () => {
    setFilterReport(defaultFilter);
  };

  const handleExportReport = () => {
    mutateExportReport({
      ...filterReport,
      doctor_id: filterReport?.doctor_id?.value,
    });
  };

  return {
    filterReport,
    totalPage,
    totalEntries,
    dataReportVisit,
    isLoading,
    isLoadingExport,
    handlePagination,
    handleClickFilter,
    handleChangeFilter,
    handleClearFilter,
    handleExportReport,
    handleChangeSizePerPage,
  };
}
